import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";

const menuItems = [
  { label: "Dashboard", icon: "ri-dashboard-line", path: "/admin/dashboard" },
  { label: "Produtos", icon: "ri-box-3-line", path: "/admin/produtos" },
  { label: "Categorias", icon: "ri-price-tag-3-line", path: "/admin/categorias" },
  { label: "Serviços", icon: "ri-settings-3-line", path: "/admin/servicos" },
  { label: "Depoimentos", icon: "ri-chat-quote-line", path: "/admin/depoimentos" },
  { label: "Cases", icon: "ri-folder-image-line", path: "/admin/cases" },
  { label: "Clientes", icon: "ri-building-line", path: "/admin/clientes" },
  { label: "Banners", icon: "ri-image-line", path: "/admin/banners" },
];

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userEmail, setUserEmail] = useState("");

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (!data.user) navigate("/admin");
      else setUserEmail(data.user.email ?? "");
    });
  }, [navigate]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/admin");
  };

  return (
    <div className="min-h-screen bg-[#f5f5f5] flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? "w-60" : "w-16"} bg-[#1a1a1a] flex flex-col transition-all duration-300 flex-shrink-0 min-h-screen`}>
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 py-5 border-b border-white/10">
          <div className="w-8 h-8 bg-[#1B1F5E] rounded flex items-center justify-center flex-shrink-0">
            <span className="font-rajdhani font-black text-white text-base leading-none">N</span>
          </div>
          {sidebarOpen && (
            <div className="flex flex-col leading-none">
              <span className="font-rajdhani font-black text-white text-sm tracking-tight">NEW TECH</span>
              <span className="text-[#3FA7D6] text-[8px] font-semibold tracking-widest uppercase">Admin</span>
            </div>
          )}
        </div>

        {/* Menu */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {menuItems.map((item) => {
            const active = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 mx-2 rounded-lg mb-1 transition-colors cursor-pointer ${active ? "bg-[#1B1F5E] text-white" : "text-white/60 hover:text-white hover:bg-white/10"}`}
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-base`}></i>
                </div>
                {sidebarOpen && <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User */}
        <div className="border-t border-white/10 p-4">
          {sidebarOpen && (
            <div className="mb-3">
              <p className="text-white/40 text-xs">Logado como</p>
              <p className="text-white text-xs font-medium truncate">{userEmail}</p>
            </div>
          )}
          <button
            onClick={handleLogout}
            className={`flex items-center gap-2 text-white/60 hover:text-red-400 transition-colors cursor-pointer ${sidebarOpen ? "w-full" : "justify-center"}`}
          >
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-logout-box-line text-base"></i>
            </div>
            {sidebarOpen && <span className="text-sm">Sair</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-[#1B1F5E] transition-colors cursor-pointer"
          >
            <i className="ri-menu-line text-lg"></i>
          </button>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              target="_blank"
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#1B1F5E] transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-external-link-line text-sm"></i>
              </div>
              Ver site
            </Link>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
